import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { AlertCircle, CircleCheckBig } from 'lucide-react'
import { AccountType } from '@/common/types'
import { APP_ROUTES } from '@/common/constants'
import { KycProviders } from '@/kyc/types'
import { useInitAccountKYC, useInitAccountParticipantKYC } from '@/kyc/kycQueries'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { analytics } from '@/common/analytics/analytics'
import { ACCOUNT_TYPE_LABELS, APPLICATION_COPY, ONBOARDING_COPY } from '@/locales/en'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { KycInitiationView } from '@/kyc/components/KycInitiationView'
import { Logo } from '@/common/assets/svg'
import { Button } from '@/common/ui'
import { ProgressBar } from '@/common/ui/progressBar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/common/ui/dialog'
import { resolveHomeRouteForContext } from '@/common/navigation/accountRoutes'

type KycConfirmPageState = {
    accountType: AccountType
    createdAccountId: string
    participantId?: string
    isAdviser: boolean
}

function AdviserConfirmationAlert() {
    return (
        <div className="w-full self-stretch p-4 rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline inline-flex justify-start items-center gap-2.5 overflow-hidden">
            <div className="flex-1 flex justify-start items-start gap-4">
                <AlertCircle size={20} className="w-5 h-5 text-content-primary flex-shrink-0" />
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                    <div className="self-stretch justify-center text-content-primary text-sm font-medium font-['Inter'] leading-5">
                        {APPLICATION_COPY.confirmation.adviserWarningTitle}
                    </div>
                    <div className="self-stretch justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                        {APPLICATION_COPY.confirmation.adviserWarningDescription}
                    </div>
                </div>
            </div>
        </div>
    )
}

export function ApplicationKycConfirmPage() {
    const location = useLocation()
    const navigate = useNavigate()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const state = location.state as KycConfirmPageState | null

    const initKyc = useInitAccountKYC()
    const initAccountParticipantKyc = useInitAccountParticipantKYC()
    const [openRedirect, setOpenRedirect] = useState(false)

    const isPending = initKyc.isPending || initAccountParticipantKyc.isPending
    const isAccountContextReady = Boolean(state?.createdAccountId && activeAccountId === state.createdAccountId)

    useEffect(() => {
        if (!state?.accountType || !state.createdAccountId) {
            navigate(APP_ROUTES.APPLICATION_RESUME, { replace: true })
        }
    }, [state, navigate])

    useEffect(() => {
        if (!initKyc.isError && !initAccountParticipantKyc.isError) return
        analytics.trackWithDefaults('add_account_account_type_initiate_failed')
        toast.error(ONBOARDING_COPY.kycInit.errorToast)
        setOpenRedirect(false)
    }, [initKyc.isError, initAccountParticipantKyc.isError])

    useEffect(() => {
        const response = initAccountParticipantKyc.data ?? initKyc.data
        if (!response) return

        if (response.provider === KycProviders.FRANKIEONE && !response.url) {
            setOpenRedirect(false)
            navigate(APP_ROUTES.APPLICATION_KYC, {
                state: state?.participantId ? { participantId: state.participantId } : undefined,
            })
            return
        }

        if (!response.provider) {
            analytics.trackWithDefaults('add_account_account_type_initiate_failed')
            toast.error(ONBOARDING_COPY.kycInit.errorToast)
            setOpenRedirect(false)
            return
        }

        const kycUrl = response.url
        if (!kycUrl) {
            analytics.trackWithDefaults('add_account_account_type_initiate_failed')
            toast.error(ONBOARDING_COPY.kycInit.errorToast)
            setOpenRedirect(false)
            return
        }

        window.location.href = kycUrl
    }, [initKyc.data, initAccountParticipantKyc.data, navigate, state?.participantId])

    if (!state?.accountType || !state.createdAccountId) return <FullScreenLoader />

    const handleStartKyc = () => {
        if (!isAccountContextReady) return
        setOpenRedirect(true)

        const provider = state.accountType === AccountType.INDIVIDUAL ? KycProviders.FRANKIEONE : KycProviders.SWIFTID

        analytics.trackWithDefaults('add_account_account_type_submitted', {
            account_type: state.accountType,
            provider,
        })

        if (state.isAdviser && state.participantId) {
            initAccountParticipantKyc.mutate({
                accountId: state.createdAccountId,
                accountType: state.accountType,
                participantId: state.participantId,
                provider,
            })
            return
        }

        initKyc.mutate({
            accountId: state.createdAccountId,
            provider,
            accountType: state.accountType,
        })
    }

    const handleSkip = () => {
        navigate(
            resolveHomeRouteForContext({
                primaryAccount: primaryAccount ?? null,
                activeAccountId,
            }),
        )
    }

    const handleCancel = () => {
        navigate(state.isAdviser ? APP_ROUTES.CLIENTS : APP_ROUTES.DASHBOARD)
    }

    return (
        <Dialog open>
            <DialogContent className="m-0 h-full w-full rounded-none grid-rows-[auto_1fr] grid gap-6 top-0 translate-y-0 pb-24 sm:pb-0 p-6 lg:p-6 min-w-0">
                <DialogHeader>
                    <div className="self-stretch w-full max-w-[600px] mx-auto flex justify-between items-center">
                        <DialogTitle className="sr-only">{APPLICATION_COPY.confirmation.title}</DialogTitle>
                        <Logo width={128} height={40} />
                        <Button
                            variant="ghost"
                            disabled={isPending}
                            onClick={handleCancel}
                            data-icon-left="false"
                            data-icon-right="false"
                            data-show-badge="false"
                            data-show-text="true"
                            data-size="default"
                            data-state="default"
                            data-type="ghost"
                            className="h-14 py-2.5 bg-white/0 rounded-[100px] flex justify-center items-center gap-2.5 border-0"
                        >
                            <span className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                                Cancel
                            </span>
                        </Button>
                    </div>
                </DialogHeader>
                <div className="w-full max-w-[600px] mx-auto flex flex-col min-h-0">
                    <ProgressBar progress={100} />
                    <div className="flex flex-col items-start gap-6 mt-6 md:mt-12 mb-10 pb-28 md:pb-0 overflow-auto">
                        <CircleCheckBig className="text-primary-light size-12" />
                        <KycInitiationView
                            title={APPLICATION_COPY.confirmation.title}
                            description={
                                state.isAdviser
                                    ? APPLICATION_COPY.create.adviserDescription
                                    : APPLICATION_COPY.create.description
                            }
                            accountType={state.accountType}
                            frankieOneOnboarding={state.accountType === AccountType.INDIVIDUAL}
                            requirementTitle={APPLICATION_COPY.create.requirementTitle}
                            requirementSubtitle={APPLICATION_COPY.create.requirementSubtitle.replace(
                                '{accountType}',
                                ACCOUNT_TYPE_LABELS[state.accountType].toLowerCase(),
                            )}
                            alert={state.isAdviser && <AdviserConfirmationAlert />}
                            headingLevel="h2"
                        />
                    </div>
                    <div
                        className="w-full bg-white p-6 flex flex-col md:flex-row gap-2.5 z-10
                        fixed bottom-0 left-0 sm:static sm:p-0 sm:bg-transparent
                        md:justify-between md:items-center"
                    >
                        <Button
                            className="h-14 border-0 order-2 md:order-1 md:w-auto w-full"
                            variant="ghost"
                            onClick={handleSkip}
                        >
                            {ONBOARDING_COPY.kycInit.secondaryButton}
                        </Button>
                        <Button
                            className="h-14 order-1 md:order-2 md:w-auto w-full"
                            variant="secondary"
                            autoFocus
                            onClick={handleStartKyc}
                            disabled={isPending || !isAccountContextReady}
                            loading={openRedirect}
                        >
                            {ONBOARDING_COPY.kycInit.primaryButton}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
